import { useMutation } from '@tanstack/react-query';
import React from 'react';
import toast from 'react-hot-toast';
import { IntlShape, useIntl } from 'react-intl';

import { getPurchaseHistoryCSV } from 'apis/merchant-gateway/merchant-analytics/api';
import type { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { EntityType } from 'common/types';
import { downloadEncodedCsv, getErrorContent } from 'common/utils';
import { getDateRangeDelta } from 'common/utils/time/formatting';

import useDatePeriod from './useDatePeriod';
import { useLanguage } from '../../../locale';
import { getIntervalForThePeriod } from '../../merchant/utils';
import Toast from '../components/Toast';
import { purchaseCsvDownloadError, purchaseCsvDownloadView } from '../telemetry/views';
import { toByDateCSV, getLocalizedPurchases, getCSVFileName, getCSVFileNameV2 } from '../utils';

const TOAST_ID = 'download_brand_purchase_data_csv';

const displayLoadingToast = (intl: IntlShape) => {
  const title = intl.formatMessage({ id: 'purchases.purchase-data-report.download-status.loading.title' });
  const body = intl.formatMessage({ id: 'purchases.purchase-data-report.download-status.loading.body' });

  toast.custom(<Toast variant={'info'} title={title} message={body} toastId={TOAST_ID} />, {
    id: TOAST_ID,
  });
};

const displayErrorToast = (intl: IntlShape, mutationError: any) => {
  const title = intl.formatMessage({ id: 'purchases.purchase-data-report.download-status.error-fallback.title' });
  const fallbackError = intl.formatMessage({
    id: 'purchases.purchase-data-report.download-status.error-fallback.body',
  });
  const parsedError = getErrorContent(mutationError);
  const errorMessage = parsedError || fallbackError;

  toast.custom(<Toast variant={'error'} title={title} message={errorMessage} toastId={TOAST_ID} />, {
    duration: Infinity,
    id: TOAST_ID,
  });
};

const displaySuccessToast = (intl: IntlShape) => {
  const title = intl.formatMessage({ id: 'purchases.purchase-data-report.download-status.success.title' });

  toast.custom(<Toast variant={'success'} title={title} message={null} toastId={TOAST_ID} />, {
    id: TOAST_ID,
  });
};

export function useDownloadBrandPurchasedData(
  venues: Venue[] | null,
  selectedVenueIds: string[],
  brandId: string,
  prefixFileName: string,
  entityType: EntityType,
) {
  const startTime = performance.now();
  const intl = useIntl();
  const { downloadPeriod, dateRange } = useDatePeriod();
  const uiLanguage = useLanguage();
  const { mutate: downloadPurchaseData, isLoading: isDownloading } = useMutation(
    async () => {
      if (!venues) {
        throw new Error('No venues for downloading data! This should not happen!');
      }

      displayLoadingToast(intl);

      const localizedPurchases = await getLocalizedPurchases(venues, selectedVenueIds, downloadPeriod, dateRange, intl);
      const venuesToDownload = venues.filter(venue => selectedVenueIds.includes(venue.id));
      const csvData = toByDateCSV(localizedPurchases, venuesToDownload[0].timezone, intl);
      const filename = getCSVFileName(prefixFileName, downloadPeriod, intl);
      downloadEncodedCsv(csvData, 'text/csv', filename, uiLanguage.key === 'ja');
    },
    {
      onError: (mutationError: any) => {
        const { daysDiff, rangeStartDaysAgo, rangeEndDaysAgo } = getDateRangeDelta(dateRange.from, dateRange.to);
        const errorMessage = getErrorContent(mutationError);
        purchaseCsvDownloadError(
          prefixFileName,
          selectedVenueIds.length,
          entityType,
          daysDiff,
          brandId,
          rangeStartDaysAgo,
          rangeEndDaysAgo,
          errorMessage ?? '',
        );
        displayErrorToast(intl, mutationError);
      },
      onSuccess: () => {
        displaySuccessToast(intl);

        const endTime = performance.now();
        const { daysDiff, rangeStartDaysAgo, rangeEndDaysAgo } = getDateRangeDelta(dateRange.from, dateRange.to);

        purchaseCsvDownloadView(
          endTime - startTime,
          prefixFileName,
          selectedVenueIds.length,
          entityType,
          daysDiff,
          brandId,
          rangeStartDaysAgo,
          rangeEndDaysAgo,
        );
      },
    },
  );

  return { downloadPurchaseData, isDownloading };
}

/**
 * useDownloadBrandPurchasedDataV2 get data from merchant-analytics-api service's API.
 * In contrast to useDownloadBrandPurchasedData, that sends requests per a venue,
 * this function is more efficient as it can get all the data with one request.
 */
export function useDownloadBrandPurchasedDataV2(
  selectedVenueIds: string[],
  brandId: string,
  prefixFileName: string,
  entityType: EntityType,
  timeZone: string,
) {
  const intl = useIntl();

  // downloadPeriod is defined in Period class, such as yesterday, this-week, last-week, etc.
  // dateRange is a object made of 'from' and 'to' to represent a time range.
  const { downloadPeriod, dateRange } = useDatePeriod();
  const uiLanguage = useLanguage();
  const performanceStartTime = performance.now();
  const startDate = dateRange.from;
  const endDate = dateRange.to;
  const { mutate: downloadPurchaseDataV2, isLoading: isDownloadingV2 } = useMutation(
    async () => {
      if (selectedVenueIds.length === 0) {
        throw new Error('No venues for downloading data! This should not happen!');
      }

      displayLoadingToast(intl);
      const purchaseHistoryTimeInterval = getIntervalForThePeriod(downloadPeriod, timeZone, {
        from: startDate,
        to: endDate,
      });

      const localizedPurchases = await getPurchaseHistoryCSV(
        selectedVenueIds,
        purchaseHistoryTimeInterval,
        uiLanguage.key,
      );
      const filename = getCSVFileNameV2(prefixFileName, intl);
      downloadEncodedCsv(localizedPurchases, 'text/csv', filename, uiLanguage.key === 'ja');
    },
    {
      onError: (mutationError: any) => {
        const { daysDiff, rangeStartDaysAgo, rangeEndDaysAgo } = getDateRangeDelta(startDate, endDate);
        const errorMessage = getErrorContent(mutationError);
        purchaseCsvDownloadError(
          prefixFileName,
          selectedVenueIds.length,
          entityType,
          daysDiff,
          brandId,
          rangeStartDaysAgo,
          rangeEndDaysAgo,
          errorMessage ?? '',
        );
        displayErrorToast(intl, mutationError);
      },
      onSuccess: () => {
        displaySuccessToast(intl);

        const performanceEndTime = performance.now();
        const { daysDiff, rangeStartDaysAgo, rangeEndDaysAgo } = getDateRangeDelta(startDate, endDate);

        purchaseCsvDownloadView(
          performanceEndTime - performanceStartTime,
          prefixFileName,
          selectedVenueIds.length,
          entityType,
          daysDiff,
          brandId,
          rangeStartDaysAgo,
          rangeEndDaysAgo,
        );
      },
    },
  );

  return { downloadPurchaseDataV2, isDownloadingV2 };
}
